import { Loadable } from '@/components/Loadable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/primitives/Dialog';
import {
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetRoot,
  SheetTitle,
  SheetTrigger,
} from '@/components/primitives/Sheet';
import { breakpoints } from '@/utils/breakpoints';
import { type FC, type ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';

interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  loading?: boolean;
  trigger?: ReactNode;
  title: ReactNode;
  content: ReactNode;
  footer?: ReactNode;
}

const ResponsiveDialog: FC<ResponsiveDialogProps> = ({
  open,
  onOpenChange,
  loading = false,
  trigger,
  title,
  content,
  footer,
}) => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  if (isLaptopOrBigger) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent className="w-[650px] max-w-none">
          <DialogHeader>
            <DialogTitle asChild>{title}</DialogTitle>
            <DialogDescription className="sr-only" asChild>
              {title}
            </DialogDescription>
          </DialogHeader>
          <Loadable
            loading={loading}
            persistent
            className={{
              persistent: 'max-h-[900px] min-h-[500px] overflow-auto',
            }}
          >
            {content}
          </Loadable>
          {footer && <DialogFooter>{footer}</DialogFooter>}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <SheetRoot open={open} onOpenChange={onOpenChange}>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent className="min-w-full">
        <SheetHeader className="shadow-md" showBack>
          <SheetTitle asChild>{title}</SheetTitle>
          <SheetDescription className="sr-only" asChild>
            {title}
          </SheetDescription>
        </SheetHeader>
        <Loadable
          loading={loading}
          persistent
          className={{ persistent: 'flex-1 overflow-auto' }}
        >
          {content}
        </Loadable>
        {footer && <SheetFooter>{footer}</SheetFooter>}
      </SheetContent>
    </SheetRoot>
  );
};

export default ResponsiveDialog;
